/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useMemo } from 'react';

import { useIntl, defineMessages } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton, LinkButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { ConfluenceLogo, JiraLogo, LoomLogo } from '@atlaskit/logo';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	type RecommendedApp,
	type ActionTier,
} from '@post-office/message-template--twc-sidenav-banner';

import { MISSING_APP_EXPERIMENT } from '../utils/missing-app-experiment';

const styles = cssMap({
	root: {
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.small'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		marginTop: token('space.150'),
		marginRight: token('space.150'),
		marginBottom: token('space.150'),
		marginLeft: token('space.150'),
	},
});

const messages = defineMessages({
	subheadingConfluence: {
		id: 'twc.sidenav.banner.personalized.subheading.confluence',
		defaultMessage: 'Create with Rovo',
		description:
			'Personalized banner subheading when the recommended app is Confluence. Shown in the product sidenav.',
	},
	subheadingJira: {
		id: 'twc.sidenav.banner.personalized.subheading.jira',
		defaultMessage: 'Plan your next team project',
		description:
			'Personalized banner subheading when the recommended app is Jira. Shown in the product sidenav.',
	},
	subheadingLoom: {
		id: 'twc.sidenav.banner.personalized.subheading.loom',
		defaultMessage: 'Set up video meetings',
		description:
			'Personalized banner subheading when the recommended app is Loom. Shown in the product sidenav.',
	},
	logoLabelConfluence: {
		id: 'twc.sidenav.banner.personalized.logo.label.confluence',
		defaultMessage: 'Confluence',
		description: 'Accessible label for the Confluence product logo lockup. Read by screen readers.',
	},
	logoLabelJira: {
		id: 'twc.sidenav.banner.personalized.logo.label.jira',
		defaultMessage: 'Jira',
		description: 'Accessible label for the Jira product logo lockup. Read by screen readers.',
	},
	logoLabelLoom: {
		id: 'twc.sidenav.banner.personalized.logo.label.loom',
		defaultMessage: 'Loom',
		description: 'Accessible label for the Loom product logo lockup. Read by screen readers.',
	},
	ctaButton: {
		id: 'twc.sidenav.banner.personalized.cta',
		defaultMessage: 'Get started',
		description:
			'CTA button text for the personalized missing-app activation banner in the product sidenav.',
	},
	closeLabel: {
		id: 'twc.sidenav.banner.personalized.close',
		defaultMessage: 'Close',
		description:
			'Accessible label for the close button on the personalized missing-app activation banner.',
	},
});

const subheadingByApp: Record<RecommendedApp, (typeof messages)[keyof typeof messages]> = {
	confluence: messages.subheadingConfluence,
	jira: messages.subheadingJira,
	loom: messages.subheadingLoom,
};

const logoLabelByApp: Record<RecommendedApp, (typeof messages)[keyof typeof messages]> = {
	confluence: messages.logoLabelConfluence,
	jira: messages.logoLabelJira,
	loom: messages.logoLabelLoom,
};

export type PersonalizedBannerProps = {
	href: string;
	locale?: string;
	recommendedApp: RecommendedApp;
	messageInstanceId?: string;
	onClose?: () => void;
	onCtaClick?: () => void;
	actionTier?: ActionTier;
	targetTenantId?: string;
	collectionEntitlementId?: string;
};

export const PersonalizedBanner = ({
	href,
	locale,
	recommendedApp,
	messageInstanceId,
	onClose,
	onCtaClick,
	actionTier,
	targetTenantId,
	collectionEntitlementId,
}: PersonalizedBannerProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const logoLabel = formatMessage(logoLabelByApp[recommendedApp]);
	const logoByApp: Record<RecommendedApp, JSX.Element> = {
		confluence: (
			<ConfluenceLogo shouldUseNewLogoDesign appearance="brand" size="xsmall" label={logoLabel} />
		),
		jira: <JiraLogo shouldUseNewLogoDesign appearance="brand" size="xsmall" label={logoLabel} />,
		loom: <LoomLogo shouldUseNewLogoDesign appearance="brand" size="xsmall" label={logoLabel} />,
	};
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const analyticsAttributes = useMemo(
		() => ({
			recommendedApp,
			messageInstanceId,
			locale,
			experimentKey: MISSING_APP_EXPERIMENT,
			actionTier,
			targetTenantId,
			collectionEntitlementId,
			arm: 'personalized' as const,
		}),
		[
			recommendedApp,
			messageInstanceId,
			locale,
			actionTier,
			targetTenantId,
			collectionEntitlementId,
		],
	);

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: 'twcPersonalizedBanner',
			attributes: analyticsAttributes,
		}).fire('growth');
	}, [analyticsAttributes, createAnalyticsEvent]);

	const handleClose = () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcPersonalizedBannerClose',
			attributes: analyticsAttributes,
		}).fire('growth');
		onClose?.();
	};

	const handleCtaClick = () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcPersonalizedBannerCta',
			attributes: analyticsAttributes,
		}).fire('growth');
		onCtaClick?.();
	};

	return (
		<Box xcss={styles.root} testId="twc-personalized-banner">
			<Stack space="space.150">
				<Inline spread="space-between" alignBlock="center" space="space.150">
					<Box testId="twc-personalized-banner-logo">{logoByApp[recommendedApp]}</Box>
					<IconButton
						icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
						onClick={handleClose}
						label={formatMessage(messages.closeLabel)}
						testId="twc-personalized-banner-close"
						appearance="subtle"
					/>
				</Inline>
				<Text size="medium" testId="twc-personalized-banner-subheading">
					{formatMessage(subheadingByApp[recommendedApp])}
				</Text>
				<Inline alignInline="end">
					<LinkButton
						appearance="default"
						href={href}
						testId="twc-personalized-banner-cta"
						onClick={handleCtaClick}
						target="_blank"
					>
						{formatMessage(messages.ctaButton)}
					</LinkButton>
				</Inline>
			</Stack>
		</Box>
	);
};
